import FilterListIcon from "@mui/icons-material/FilterList";
import { Box, Button, Chip, Divider, Stack, Typography } from "@mui/material";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCustomisation } from "../../../../context/CustomisationContext";
import useScreenSize from "../../../../hooks/useScreenSize";
import { gray } from "../../../../theme/themePrimitives";
import { getShadeColour } from "../../../../utils/helpers";
import { AssignmentsFilter, FilterType } from "./components/AssignmentsFilter";
import { AssignmentCard } from "./components/AssignmentCard";
import { PrerequisitesModal } from "./components/PrerequisitesModal";
import { useTranslation } from "../../../../context/TranslationContext";
import { usePortal } from "../../../../context/PortalsContext";
import {
  HomeAssignment,
  ProgressStatus,
  AssignmentType,
  ImportanceType,
  GetHomeAssignmentsParams,
  UserAssignmentsMode,
} from "../../../../services/api/types/general/homeAssignment";
import { PrerequisiteSummaryResult } from "../../../../services/api/types/assignments/assignment";
import { getPrerequisiteSummaryResult } from "../../../../services/api/apiClient";

interface MyAssignmentsProps {
  assignments: HomeAssignment[];
  allAssignments?: HomeAssignment[];
  onCardClick: (assignment: HomeAssignment) => void;
  onFiltersChange?: (filters: GetHomeAssignmentsParams) => void;
  groupLevel?: number;
  activeFilters?: GetHomeAssignmentsParams;
  isPreviewMode?: boolean;
  autoOpenAssignmentUid?: string;
}

export const MyAssignments: React.FC<MyAssignmentsProps> = ({
  assignments,
  allAssignments,
  onCardClick,
  onFiltersChange,
  groupLevel = 0,
  activeFilters,
  isPreviewMode: isPreviewModeProp,
  autoOpenAssignmentUid,
}) => {
  const { translations } = useTranslation();
  const homeTranslations = translations.translations.home;
  const [showFilter, setShowFilter] = useState(false);

  // Derive available groups from ALL assignments (before filtering) for the filter options
  // Use allAssignments if provided, otherwise fall back to assignments
  const availableGroups = useMemo(() => {
    const sourceAssignments = allAssignments || assignments;
    return sourceAssignments.filter((a) => !a.isDirectAssignment);
  }, [allAssignments, assignments]);

  const isPreviewMode = useMemo(() => {
    if (isPreviewModeProp !== undefined) return isPreviewModeProp;
    const source = allAssignments?.length ? allAssignments : assignments;
    return source.some((a) => a.mode === UserAssignmentsMode.PREVIEW);
  }, [assignments, allAssignments, isPreviewModeProp]);

  const addAllCheckbox = (
    items: FilterType["items"],
    filterKey: string,
  ): FilterType["items"] => {
    // "All" is checked only if all non-All items are checked
    const allChecked = items.length > 0 && items.every((item) => item.checked);
    return [
      {
        id: `${filterKey}-all`,
        name: "All",
        checked: allChecked,
        value: undefined,
        isAll: true,
      },
      ...items,
    ];
  };

  // Helper to check if a value is selected in active filters
  const isValueSelected = (
    filterKey: string,
    value: string | undefined,
  ): boolean => {
    if (!activeFilters || !value) return false;

    let activeValue: string | undefined;
    if (filterKey === "groups") {
      activeValue = activeFilters.portalGroupId;
    } else if (filterKey === "progressStatus") {
      activeValue = activeFilters.progressStatus;
    } else if (filterKey === "type") {
      activeValue = activeFilters.type;
    } else if (filterKey === "importance") {
      activeValue = activeFilters.importance;
    }

    if (!activeValue) return false;
    return activeValue.split("|").includes(value);
  };

  const filtersData = useMemo<FilterType[]>(() => {
    // Build group items from currently visible groups (non-direct assignments)
    // Only show groups filter if we're at groupLevel < 2 and there are groups available
    const groupItems: FilterType["items"] = availableGroups.map((group) => ({
      id: `group-${group.assignmentId}`,
      name: group.title,
      checked: isValueSelected("groups", group.assignmentId),
      value: group.assignmentId,
    }));

    const filters: FilterType[] = [];

    // Only include groups filter if groupLevel < 2 and there are groups to filter
    if (groupLevel < 2 && groupItems.length > 0) {
      filters.push({
        title: "Groups",
        filterKey: "groups",
        items: addAllCheckbox(groupItems, "groups"),
      });
    }

    filters.push({
      title: "Progress Status",
      filterKey: "progressStatus",
      items: addAllCheckbox(
        [
          {
            id: "progress-not-started",
            name: "Not Started",
            checked: isValueSelected(
              "progressStatus",
              ProgressStatus.NOTSTARTED,
            ),
            value: ProgressStatus.NOTSTARTED,
          },
          {
            id: "progress-in-progress",
            name: "In Progress",
            checked: isValueSelected(
              "progressStatus",
              ProgressStatus.INPROGRESS,
            ),
            value: ProgressStatus.INPROGRESS,
          },
          {
            id: "progress-completed",
            name: "Completed",
            checked: isValueSelected(
              "progressStatus",
              ProgressStatus.COMPLETED,
            ),
            value: ProgressStatus.COMPLETED,
          },
          {
            id: "progress-failed",
            name: "Failed",
            checked: isValueSelected("progressStatus", ProgressStatus.FAILED),
            value: ProgressStatus.FAILED,
          },
          {
            id: "progress-not-started-overdue",
            name: "Not Started (Overdue)",
            checked: isValueSelected(
              "progressStatus",
              ProgressStatus.NOTSTARTED_OVERDUE,
            ),
            value: ProgressStatus.NOTSTARTED_OVERDUE,
          },
          {
            id: "progress-in-progress-overdue",
            name: "In Progress (Overdue)",
            checked: isValueSelected(
              "progressStatus",
              ProgressStatus.INPROGRESS_OVERDUE,
            ),
            value: ProgressStatus.INPROGRESS_OVERDUE,
          },
          {
            id: "progress-completed-overdue",
            name: "Completed (Overdue)",
            checked: isValueSelected(
              "progressStatus",
              ProgressStatus.FAILED_OVERDUE,
            ),
            value: ProgressStatus.FAILED_OVERDUE,
          },
        ],
        "progressStatus",
      ),
    });

    filters.push({
      title: "Assignment Type",
      filterKey: "type",
      items: addAllCheckbox(
        [
          {
            id: "type-course",
            name: "Course",
            checked: isValueSelected("type", AssignmentType.COURSE),
            value: AssignmentType.COURSE,
          },
          {
            id: "type-policy",
            name: "Policy",
            checked: isValueSelected("type", AssignmentType.POLICY),
            value: AssignmentType.POLICY,
          },
          {
            id: "type-survey",
            name: "Survey",
            checked: isValueSelected("type", AssignmentType.SURVEY),
            value: AssignmentType.SURVEY,
          },
        ],
        "type",
      ),
    });

    filters.push({
      title: "Importance",
      filterKey: "importance",
      items: addAllCheckbox(
        [
          {
            id: "importance-required",
            name: "Required",
            checked: isValueSelected("importance", ImportanceType.REQUIRED),
            value: ImportanceType.REQUIRED,
          },
          {
            id: "importance-optional",
            name: "Optional",
            checked: isValueSelected("importance", ImportanceType.OPTIONAL),
            value: ImportanceType.OPTIONAL,
          },
        ],
        "importance",
      ),
    });

    return filters;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableGroups, groupLevel, activeFilters]);

  const [showChips, setShowChips] = useState(false);
  const [filtersList, setFiltersList] = useState<FilterType[]>(filtersData);
  const [savedFilters, setSavedFilters] = useState<FilterType[]>([]);

  // Determine if there are any active filters to show chips
  const hasActiveFilters = useMemo(() => {
    if (!activeFilters) return false;
    return !!(
      activeFilters.portalGroupId ||
      activeFilters.type ||
      activeFilters.importance ||
      activeFilters.progressStatus
    );
  }, [activeFilters]);

  // Sync filtersList and savedFilters when filtersData changes (e.g., after navigation)
  useEffect(() => {
    if (!showFilter) {
      setFiltersList(filtersData);
    }

    if (hasActiveFilters) {
      setSavedFilters(filtersData);
      setShowChips(true);
    } else {
      setSavedFilters([]);
      setShowChips(false);
    }
  }, [filtersData, hasActiveFilters, showFilter]);

  const { isMobile } = useScreenSize();
  const { customisations } = useCustomisation();
  const {
    HOME: {
      textColorsSettings: homeTextColors,
      backgroundSettings: homeBgSettings,
      buttonsSettings: homebuttonssettings,
      assignmentStatusColorsSettings: homeAssignmentSettings,
      cardsSettings,
    },
    GPC: {
      textColorsSettings: gpcTextColors,
      backgroundSettings: gpcBgSettings,
      buttonsSettings: gpcbuttonssettings,
      assignmentStatusColorsSettings: gpcAssignmentSettings,
    },
  } = customisations.customisations;
  const primaryTextColor =
    homeTextColors?.homePrimaryTextColor ?? gpcTextColors?.gpcPrimaryTextColor;
  const secondaryTextcolor =
    homeTextColors?.homeSecondaryTextColor ??
    gpcTextColors?.gpcSecondaryTextColor;
  const linkColor =
    homebuttonssettings?.homeLinkColor ?? gpcbuttonssettings?.gpcLinkColor;
  const primaryButtonColor =
    homebuttonssettings?.homeBtnPrimaryBgColor ??
    gpcbuttonssettings?.gpcBtnPrimaryBgColor;
  const primaryButtonText =
    homebuttonssettings?.homeBtnPrimaryTextColor ??
    gpcbuttonssettings?.gpcBtnPrimaryTextColor;
  const coursesColor =
    cardsSettings?.cardCourseColor ??
    homeAssignmentSettings?.homeCompleted ??
    gpcAssignmentSettings?.gpcCompleted;
  const surveysColor =
    cardsSettings?.cardSurveyColor ??
    homeAssignmentSettings?.homePending ??
    gpcAssignmentSettings?.gpcPending;
  const policiesColor =
    cardsSettings?.cardPolicyColor ??
    homeAssignmentSettings?.homeOverdue ??
    gpcAssignmentSettings?.gpcOverdue;
  const overdueColor =
    cardsSettings?.cardOverdueTextColor ??
    homeAssignmentSettings?.homeOverdue ??
    gpcAssignmentSettings?.gpcOverdue;
  const overdueShade = getShadeColour(overdueColor!, 55);
  const dateColor =
    cardsSettings?.cardDateTextColor ??
    homeAssignmentSettings?.homeCompleted ??
    gpcAssignmentSettings?.gpcCompleted;
  const dateShade = getShadeColour(dateColor!, 25);
  const cardBgColor =
    cardsSettings?.cardBackgroundColor ??
    homeBgSettings?.homeSecondaryBgColor ??
    gpcBgSettings?.gpcSecondaryBgColor;
  const notStartedColor = cardsSettings?.cardNotStartedProgressBarColor;
  const inProgressColor = cardsSettings?.cardInProgressBarColor;
  const completedColor = cardsSettings?.cardCompletedProgressBarColor;
  const useOpacity = cardsSettings?.cardTransparencyLevel ? true : false;

  const opacity = useOpacity
    ? Number(cardsSettings?.cardTransparencyLevel) / 100
    : 0;

  const getProgressColor = (progressStatus: string) => {
    if (progressStatus === ProgressStatus.COMPLETED) {
      return completedColor;
    } else if (progressStatus === ProgressStatus.NOTSTARTED) {
      return getShadeColour(notStartedColor!, 20);
    } else {
      return inProgressColor;
    }
  };

  const navigate = useNavigate();
  const location = useLocation();
  const { portals } = usePortal();
  const [lockedModalAssignment, setLockedModalAssignment] =
    useState<HomeAssignment | null>(null);
  const [prerequisiteData, setPrerequisiteData] = useState<
    PrerequisiteSummaryResult[]
  >([]);
  const [prerequisiteLoading, setPrerequisiteLoading] = useState(false);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const lastAutoOpenUid = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!lockedModalAssignment || !portals.selectedPortal) {
      setPrerequisiteData([]);
      return;
    }

    const fetchPrerequisites = async () => {
      try {
        setPrerequisiteLoading(true);

        const results = await getPrerequisiteSummaryResult(
          portals.selectedPortal!.portalUid,
          lockedModalAssignment.assignmentId,
        );
        setPrerequisiteData(results);
      } catch {
        setPrerequisiteData([]);
      } finally {
        setPrerequisiteLoading(false);
      }
    };

    fetchPrerequisites();
  }, [lockedModalAssignment, portals.selectedPortal]);

  useEffect(() => {
    if (!autoOpenAssignmentUid || assignments.length === 0) return;
    if (lastAutoOpenUid.current === autoOpenAssignmentUid) return;

    const target = assignments.find(
      (a) => a.assignmentId === autoOpenAssignmentUid,
    );
    if (!target) return;

    lastAutoOpenUid.current = autoOpenAssignmentUid;
    setLockedModalAssignment(target);

    navigate(location.pathname + location.search, {
      replace: true,
      state: null,
    });

    requestAnimationFrame(() => {
      cardRefs.current
        .get(autoOpenAssignmentUid)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [assignments, autoOpenAssignmentUid]);

  const handleCardClick = (assignment: HomeAssignment) => {
    if (assignment.isLocked) {
      setLockedModalAssignment(assignment);
    } else {
      onCardClick(assignment);
    }
  };

  const handlePrerequisiteClick = (prereq: PrerequisiteSummaryResult) => {
    setLockedModalAssignment(null);

    if (prereq.isDirectAssignment) {
      navigate(`/assignment/${prereq.userAssignmentUid}`, {
        state: {
          parentPath: location.pathname + location.search,
          mode: UserAssignmentsMode.ENROLLED,
        },
      });
    } else {
      const { groupLevel, portalGroupId, parentPortalGroupId } =
        prereq.queryParams;
      const level = parseInt(groupLevel, 10);
      const autoOpenState = { autoOpenAssignmentUid: prereq.userAssignmentUid };

      if (level === 0 || !portalGroupId) {
        navigate("/home", { state: autoOpenState });
      } else if (level >= 2 && parentPortalGroupId) {
        navigate(`/home/assignments/${parentPortalGroupId}/${portalGroupId}`, {
          state: autoOpenState,
        });
      } else {
        navigate(`/home/assignments/${portalGroupId}`, {
          state: autoOpenState,
        });
      }
    }
  };

  const handleCheckboxChange = (index: number, itemIndex: number) => {
    setFiltersList((prev) =>
      prev.map((filter, filterIndex) => {
        if (filterIndex !== index) return filter;

        const clickedItem = filter.items[itemIndex];
        const isAllCheckbox = clickedItem.isAll === true;

        let updatedItems = filter.items.map((item) => ({ ...item }));

        if (isAllCheckbox) {
          const shouldCheckAll = !clickedItem.checked;
          updatedItems = updatedItems.map((item) => ({
            ...item,
            checked: shouldCheckAll,
          }));
        } else {
          updatedItems[itemIndex].checked = !updatedItems[itemIndex].checked;

          const allCheckboxIndex = updatedItems.findIndex(
            (item) => item.isAll === true,
          );

          if (allCheckboxIndex !== -1) {
            const otherItems = updatedItems.filter(
              (item) => item.isAll !== true,
            );
            const allOtherChecked = otherItems.every((item) => item.checked);
            updatedItems[allCheckboxIndex].checked = allOtherChecked;
          }
        }

        return {
          ...filter,
          items: updatedItems,
        };
      }),
    );
  };

  const clearFilters = () => {
    const updatedFilters = [...filtersList];
    updatedFilters.map((filter) =>
      filter.items.map((item) => (item.checked = false)),
    );
    setFiltersList(updatedFilters);
    setSavedFilters([]);
    setShowChips(false);
    if (onFiltersChange) {
      onFiltersChange({ groupLevel: 0 });
    }
  };

  const convertFiltersToParams = (): GetHomeAssignmentsParams => {
    const params: GetHomeAssignmentsParams = { groupLevel: 0 };

    filtersList.forEach((filter) => {
      // In preview mode, only the Assignment type filter applies
      if (isPreviewMode && filter.filterKey !== "type") return;

      const checkedItems = filter.items.filter(
        (item) =>
          item.checked && item.isAll !== true && item.value !== undefined,
      );

      if (checkedItems.length === 0) {
        if (filter.filterKey === "groups") {
          params.groupLevel = 0;
        }
        return;
      }

      const valueString = checkedItems
        .map((item) => String(item.value))
        .join("|");

      if (filter.filterKey === "groups") {
        params.portalGroupId = valueString;
      } else if (filter.filterKey === "progressStatus") {
        params.progressStatus = valueString;
      } else if (filter.filterKey === "type") {
        params.type = valueString;
      } else if (filter.filterKey === "importance") {
        params.importance = valueString;
      }
    });

    return params;
  };

  const saveFilters = () => {
    setSavedFilters(JSON.parse(JSON.stringify(filtersList)));
    setShowChips(true);
    setShowFilter(false);
    const filterParams = convertFiltersToParams();
    if (onFiltersChange) {
      onFiltersChange(filterParams);
    }
  };

  const handleChipDelete = (itemIndex: number, iIndex: number) => {
    // Update both filtersList and savedFilters when deleting a chip
    const updateFilters = (prev: FilterType[]) =>
      prev.map((item, idx) => {
        if (idx !== itemIndex) return item;

        const updatedItems = item.items.map((i, iIdx) =>
          iIdx === iIndex ? { ...i, checked: !i.checked } : i,
        );

        const allCheckbox = updatedItems.find((i) => i.isAll === true);
        if (allCheckbox) {
          const otherItems = updatedItems.filter((i) => i.isAll !== true);
          const allOtherChecked = otherItems.every((i) => i.checked);
          allCheckbox.checked = allOtherChecked;
        }

        return {
          ...item,
          items: updatedItems,
        };
      });

    setFiltersList(updateFilters);
    setSavedFilters(updateFilters);

    const updatedFilters = updateFilters(filtersList);
    const filterParams: GetHomeAssignmentsParams = { groupLevel: 0 };
    updatedFilters.forEach((filter) => {
      const checkedItems = filter.items.filter(
        (item) =>
          item.checked && item.isAll !== true && item.value !== undefined,
      );

      if (checkedItems.length === 0) {
        if (filter.filterKey === "groups") {
          filterParams.groupLevel = 0;
        }
        return;
      }

      const valueString = checkedItems
        .map((item) => String(item.value))
        .join("|");

      if (filter.filterKey === "groups") {
        filterParams.portalGroupId = valueString;
      } else if (filter.filterKey === "progressStatus") {
        filterParams.progressStatus = valueString;
      } else if (filter.filterKey === "type") {
        filterParams.type = valueString;
      } else if (filter.filterKey === "importance") {
        filterParams.importance = valueString;
      }
    });

    if (onFiltersChange) {
      onFiltersChange(filterParams);
    }
  };

  const hasSelectedFilters = () => {
    if (!showChips) {
      return false;
    }
    return (
      savedFilters.length > 0 &&
      savedFilters.some((filter) =>
        filter.items.some((item) => item.checked && item.isAll !== true),
      )
    );
  };

  const renderChips = () => {
    if (!showChips || savedFilters.length === 0) {
      return null;
    }
    return savedFilters.map((item, itemIndex) =>
      item.items.map((i, iIndex) =>
        i.checked && i.isAll !== true ? (
          <Chip
            key={`${item.title}-${i.name}`}
            label={`${item.title}: ${i.name}`}
            onDelete={() => handleChipDelete(itemIndex, iIndex)}
            sx={{
              backgroundColor: primaryButtonColor,
              border: `1px solid ${gray[300]}`,
              padding: "16px 8px",
              borderRadius: "6px",
              "& .MuiChip-label": {
                color: primaryButtonText,
              },
            }}
          />
        ) : null,
      ),
    );
  };

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <Typography
          color={primaryTextColor}
          variant="h4"
          fontSize={"20px !important"}
          lineHeight={"26px !important"}
          fontWeight={"semi-bold"}
        >
          {homeTranslations.assignements?.myAssignements}
        </Typography>
        <Button
          sx={{
            backgroundColor: `${primaryButtonColor} !important`,
            color: primaryButtonText,
            borderRadius: "12px",
            padding: "16px",
            "&:hover": {
              bgcolor: `${getShadeColour(primaryButtonColor!)} !important`,
              fontWeight: 700,
            },
          }}
          startIcon={<FilterListIcon sx={{ color: primaryButtonText }} />}
          onClick={() => {
            setShowFilter(!showFilter);
          }}
        >
          {showFilter
            ? homeTranslations.assignements?.hideFilters
            : homeTranslations.assignements?.filter}
        </Button>
      </Box>
      {hasSelectedFilters() && (
        <>
          <Divider />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {!isMobile ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                <Typography
                  color={primaryTextColor}
                  sx={{ whiteSpace: "nowrap" }}
                  fontWeight={"bold"}
                >
                  {`${homeTranslations.assignements?.filtersApplied}:`}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                  {showChips && renderChips()}
                </Box>
              </Box>
            ) : (
              <Typography color={primaryTextColor} fontWeight={"bold"}>
                {`${homeTranslations.assignements?.filtersApplied}:`}
              </Typography>
            )}
            <Typography
              onClick={() => clearFilters()}
              sx={{
                color: linkColor,
                cursor: "pointer",
                whiteSpace: "nowrap",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              {homeTranslations.assignements?.clearAll}
            </Typography>
          </Box>
          {isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                {showChips && renderChips()}
              </Box>
            </Box>
          )}
        </>
      )}
      <Stack direction={"row"} spacing={2} alignItems={"flex-start"}>
        <Box
          sx={{
            display: "grid",
            width: "100%",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
              lg: "1fr 1fr 1fr 1fr",
            },
            gap: 3,
          }}
        >
          {assignments.map((assignment, index) => {
            const items = [];

            if (assignment.courseCount > 0) {
              items.push(
                <Typography key="courses" sx={{ color: coursesColor }}>
                  {assignment.courseCount}{" "}
                  {homeTranslations.assignements?.courses}
                </Typography>,
              );
            }

            if (assignment.policyCount > 0) {
              items.push(
                <Typography key="policies" sx={{ color: policiesColor }}>
                  {assignment.policyCount}{" "}
                  {homeTranslations.assignements?.policies}
                </Typography>,
              );
            }

            if (assignment.surveyCount > 0) {
              items.push(
                <Typography key="surveys" sx={{ color: surveysColor }}>
                  {assignment.surveyCount}{" "}
                  {homeTranslations.assignements?.surveys}
                </Typography>,
              );
            }

            return (
              <AssignmentCard
                key={assignment.assignmentId}
                ref={(el) => {
                  if (el) cardRefs.current.set(assignment.assignmentId, el);
                  else cardRefs.current.delete(assignment.assignmentId);
                }}
                assignment={assignment}
                onClick={handleCardClick}
                colors={{
                  backgroundColor: cardBgColor,
                  primaryText: secondaryTextcolor,
                  courses: coursesColor,
                  surveys: surveysColor,
                  policies: policiesColor,
                  overdue:
                    overdueColor && overdueShade
                      ? {
                          text: overdueColor,
                          background: overdueShade,
                        }
                      : undefined,
                  date:
                    dateColor && dateShade
                      ? {
                          text: dateColor,
                          background: dateShade,
                        }
                      : undefined,
                  progress: {
                    notStarted: notStartedColor,
                    inProgress: inProgressColor,
                    completed: completedColor,
                  },
                }}
                opacity={
                  useOpacity ? { enabled: true, value: opacity } : undefined
                }
                items={items}
                getProgressColor={getProgressColor}
              />
            );
          })}
        </Box>
        <AssignmentsFilter
          filtersList={filtersList}
          clearFilters={clearFilters}
          setShowFilter={setShowFilter}
          showFilter={showFilter}
          handleCheckboxChange={handleCheckboxChange}
          saveFilters={saveFilters}
          isPreviewMode={isPreviewMode}
        />
      </Stack>

      {lockedModalAssignment && (
        <PrerequisitesModal
          open={Boolean(lockedModalAssignment)}
          onClose={() => setLockedModalAssignment(null)}
          assignment={lockedModalAssignment}
          prerequisites={prerequisiteData}
          isLoading={prerequisiteLoading}
          colors={{
            completed: coursesColor ?? "#4CAF50",
            inProgress: surveysColor ?? "#FF9800",
            primaryButton: primaryButtonColor ?? "#4361EE",
            primaryButtonText: primaryButtonText ?? "#FFFFFF",
            link: linkColor ?? "#4361EE",
          }}
          onPrerequisiteClick={handlePrerequisiteClick}
        />
      )}
    </Stack>
  );
};
